/**
 * The app's one store: bootstrap (me, people, rooms), loaded messages per room,
 * pending invites, and the live connection. Components read it with useStore;
 * WebSocket events and API calls update it.
 */
import { useSyncExternalStore } from "react";
import { onWorkEvent } from "./work";
import { api, ApiError } from "./api";
import type { FileView } from "./files";

export type Role = "admin" | "member" | "guest";

export interface Person {
  id: string;
  name: string;
  kind: "human" | "agent";
  avatarUrl: string | null;
  role: Role;
  email: string | null;
  lastSeenAt: string | null;
  online: boolean;
  agent: { kind: "claude_channel" | "api" | "codex_cli"; model: string | null; enabled: boolean } | null;
}

export interface Room {
  id: string;
  kind: "room" | "dm";
  name: string;
  purpose: string;
  color: string;
  isPrivate: boolean;
  projectId: string | null;
  /** The agent that answers here without an @ when a message is for the agents. */
  hostAgentId: string | null;
  isMember: boolean;
  myRole: "owner" | "member" | null;
  canManage: boolean;
  canJoin: boolean;
  members: { userId: string; role: "owner" | "member" }[];
  dmWith: string | null;
  unread: number;
  mentions: number;
  lastReadMessageId: number;
  lastMessageId: number;
}

export interface Project {
  id: string;
  name: string;
  repo: string;
  defaultBranch: string;
  folderPath: string;
  liveUrl: string;
  previewPattern: string;
  business: string;
  deliveryMode: "automatic" | "manual";
  status: "active" | "paused" | "archived";
  workersEnabled: boolean;
  workerWorkflow: string;
  peopleIds: string[];
  rooms: { id: string; name: string }[];
}

export interface Message {
  id: number;
  roomId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  mentions: string[];
  reactions: { emoji: string; userIds: string[] }[];
  /** Saved as a decision. */
  decision?: boolean;
  /** A task card. */
  taskId?: string | null;
  /** Images and documents. */
  files?: FileView[];
  /** An event agent's draft: waits for an admin, then approved or discarded. */
  draft?: { status: "draft" | "approved" | "discarded"; by: string | null; at: string | null } | null;
  /** An action an agent proposes: an admin confirms or cancels it. */
  action?: AgentAction | null;
  /** Claude waits for the owner: a permission, questions or a plan (Phase 8b). */
  prompt?: ClaudePromptView | null;
}

export interface ClaudePromptView {
  id: string;
  kind: "permission" | "question" | "plan";
  tool: string;
  spec: import("@agora/agent-stream").PromptSpec;
  status: "open" | "answered" | "expired";
  decision: import("@agora/agent-stream").PromptDecision | null;
  answer: Record<string, unknown> | null;
  decidedAt: string | null;
  decidedBy: string | null;
  sessionId: string;
}

export interface AgentAction {
  id: string;
  kind: string;
  summary: string;
  details: string[];
  status: "proposed" | "running" | "done" | "cancelled" | "failed";
  result: string;
  link: string;
  agentId: string;
  requestedBy: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

export type TaskStatus = "todo" | "doing" | "review" | "done" | "cancelled";
export const STATUS_LABEL: Record<TaskStatus, string> = { todo: "To do", doing: "In progress", review: "In review", done: "Done", cancelled: "Cancelled" };

export type CiState = "success" | "failure" | "pending" | null;
export interface TaskGithub {
  issue?: { state: "open" | "closed"; title: string; url: string };
  pr?: { state: "open" | "closed"; merged: boolean; draft: boolean; title: string; url: string; branch: string; ci: CiState };
  checkedAt: string;
}

export interface Task {
  id: string;
  title: string;
  notes: string;
  status: TaskStatus;
  ownerId: string | null;
  dueDate: string | null;
  projectId: string | null;
  projectName: string | null;
  createdBy: string | null;
  sourceMessageId: number | null;
  githubIssue: number | null;
  githubPr: number | null;
  githubBranch: string | null;
  github: TaskGithub | null;
  /** The coordinator's chain (Phase 6). */
  flow: "approved" | "building" | "pr_open" | "reviewing" | "reviewed" | "confirmed" | "merged" | "live" | "manual" | "failed" | null;
  flowNote: string;
  approvedAt: string | null;
  confirmedAt: string | null;
  rooms: { id: string; name: string }[];
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
  doneAt: string | null;
}

export interface Invite {
  id: string;
  email: string;
  role: Role;
  rooms: { id: string; name: string }[];
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string;
}

interface Bootstrap {
  me: { id: string; name: string; email: string; avatarUrl: string | null; hasPassword: boolean; google: boolean; bio: string; createdAt: string; isOwner?: boolean };
  workspace: { id: string; name: string; role: Role };
  people: Person[];
  rooms: Room[];
  projects: Project[];
  colors: string[];
}

interface RoomMessages {
  items: Message[];
  hasMore: boolean;
  loading: boolean;
}

export interface State {
  status: "loading" | "ready" | "signedOut" | "noWorkspace";
  boot: Bootstrap | null;
  messages: Record<string, RoomMessages>;
  invites: Invite[] | null;
  live: boolean;
  /** A newer version of the web app is live: offer a reload. */
  updated?: boolean;
  activeRoomId: string | null;
  /** Agents working on an answer, per room. */
  thinking: Record<string, string[]>;
  /** Short lived notices from the server ("Claude is offline"), per room. */
  notices: Record<string, { id: number; text: string }[]>;
  /** A quoted message waiting to be put into a room's composer. */
  quotes: Record<string, string | null>;
  /** Goes up when any decision changes, so open lists reload. */
  decisionsVersion: number;
  /** Tasks seen so far (cards, lists), kept current by task.updated events. */
  tasks: Record<string, Task>;
  /** Goes up when any task changes, so open lists reload. */
  tasksVersion: number;
}

let state: State = { status: "loading", boot: null, messages: {}, invites: null, live: false, activeRoomId: null, thinking: {}, notices: {}, quotes: {}, decisionsVersion: 0, tasks: {}, tasksVersion: 0 };
let noticeSeq = 0;
const listeners = new Set<() => void>();

function set(patch: Partial<State> | ((s: State) => Partial<State>)) {
  state = { ...state, ...(typeof patch === "function" ? patch(state) : patch) };
  listeners.forEach((l) => l());
}

export function useStore<T>(select: (s: State) => T): T {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => select(state),
  );
}
export const getState = () => state;

/** One shared empty array, so selectors that fall back to [] stay stable for useSyncExternalStore. */
export const EMPTY: never[] = [];

const updateRoom = (id: string, fn: (r: Room) => Room) =>
  set((s) => (s.boot ? { boot: { ...s.boot, rooms: s.boot.rooms.map((r) => (r.id === id ? fn(r) : r)) } } : {}));

// ---- bootstrap and live connection ------------------------------------------

export async function loadBootstrap(): Promise<void> {
  try {
    const boot = await api<Bootstrap>("/api/bootstrap");
    set({ boot, status: "ready" });
    connect();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) set({ status: "signedOut" });
    else if (err instanceof ApiError && err.status === 403) set({ status: "noWorkspace" });
    // The server may be restarting (a deploy): try again shortly instead of staying on the loading screen.
    else setTimeout(() => void loadBootstrap(), 3000);
  }
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
function refreshSoon() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => void loadBootstrap(), 150);
}

/** The built app's script name changes with every deploy; compare it with the one this page runs. */
async function checkVersion(): Promise<void> {
  const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute("src");
  if (!mine || state.updated) return;
  try {
    const html = await fetch("/", { cache: "no-store" }).then((r) => r.text());
    const live = html.match(/\/assets\/index-[^"']+\.js/)?.[0];
    if (live && !mine.endsWith(live)) set({ updated: true });
  } catch {
    // offline: try again at the next reconnect
  }
}
if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && void checkVersion());

let socket: WebSocket | null = null;
let retry = 0;
function connect() {
  if (socket && socket.readyState <= 1) return;
  const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/ws`;
  const ws = new WebSocket(url);
  socket = ws;
  ws.onopen = () => {
    retry = 0;
  };
  ws.onmessage = (e) => handleEvent(JSON.parse(String(e.data)));
  ws.onclose = (e) => {
    set({ live: false });
    socket = null;
    if (e.code === 4401) return void set({ status: "signedOut" });
    const delay = Math.min(30_000, 1000 * 2 ** retry++);
    setTimeout(() => {
      if (state.status === "ready") connect();
    }, delay);
  };
}

export function disconnect() {
  socket?.close();
  socket = null;
}

type Event =
  | { type: "hello" }
  | { type: "message.created" | "message.updated"; message: Message }
  | { type: "read"; roomId: string; lastReadMessageId: number }
  | { type: "rooms.changed" | "people.changed" }
  | { type: "invites.changed" }
  | { type: "presence"; userId: string; online: boolean }
  | { type: "agent.state"; roomId: string; agentId: string; state: "thinking" | "idle"; requestId: string }
  | { type: "notice"; roomId: string; text: string }
  | { type: "decisions.changed"; roomId: string; projectId: string | null }
  | { type: "task.updated"; taskId: string }
  | { type: "work.event"; sessionId: string; event: import("@agora/agent-stream").WorkEvent }
  | { type: "work.session"; session: import("./work").SessionView };

function handleEvent(e: Event) {
  if (e.type === "work.event" || e.type === "work.session") return onWorkEvent(e);
  const me = state.boot?.me.id;
  switch (e.type) {
    case "hello": {
      const wasDown = !state.live && state.boot;
      set({ live: true });
      // After a reconnect, catch up on anything missed, and see whether a deploy brought a new version.
      if (wasDown) {
        refreshSoon();
        if (state.activeRoomId) void loadMessages(state.activeRoomId, { fresh: true });
        void checkVersion();
      }
      return;
    }
    case "message.created": {
      const m = e.message;
      if (m.taskId && !state.tasks[m.taskId]) void refreshTask(m.taskId);
      set((s) => {
        const list = s.messages[m.roomId];
        if (!list || list.items.some((x) => x.id === m.id)) return {};
        return { messages: { ...s.messages, [m.roomId]: { ...list, items: [...list.items, m] } } };
      });
      const watching = state.activeRoomId === m.roomId && document.visibilityState === "visible";
      updateRoom(m.roomId, (r) => {
        const mine = m.authorId === me;
        const unread = mine || watching ? r.unread : r.unread + 1;
        const mentions = !mine && !watching && me && m.mentions.includes(me) ? r.mentions + 1 : r.mentions;
        return { ...r, unread, mentions, lastMessageId: Math.max(r.lastMessageId, m.id) };
      });
      if (watching && m.authorId !== me) void markRead(m.roomId);
      if (m.authorId && state.thinking[m.roomId]?.includes(m.authorId)) {
        set((s) => ({ thinking: { ...s.thinking, [m.roomId]: (s.thinking[m.roomId] ?? []).filter((a) => a !== m.authorId) } }));
      }
      if (!state.boot?.rooms.some((r) => r.id === m.roomId)) refreshSoon();
      return;
    }
    case "message.updated": {
      const m = e.message;
      set((s) => {
        const list = s.messages[m.roomId];
        if (!list) return {};
        return { messages: { ...s.messages, [m.roomId]: { ...list, items: list.items.map((x) => (x.id === m.id ? m : x)) } } };
      });
      return;
    }
    case "read":
      updateRoom(e.roomId, (r) => ({ ...r, unread: 0, mentions: 0, lastReadMessageId: e.lastReadMessageId }));
      return;
    case "rooms.changed":
    case "people.changed":
      refreshSoon();
      return;
    case "invites.changed":
      if (state.invites) void loadInvites();
      return;
    case "agent.state":
      set((s) => {
        const now = new Set(s.thinking[e.roomId] ?? []);
        if (e.state === "thinking") now.add(e.agentId);
        else now.delete(e.agentId);
        return { thinking: { ...s.thinking, [e.roomId]: [...now] } };
      });
      return;
    case "decisions.changed":
      set((s) => ({ decisionsVersion: s.decisionsVersion + 1 }));
      return;
    case "task.updated":
      void refreshTask(e.taskId);
      return;
    case "notice": {
      const id = ++noticeSeq;
      set((s) => ({ notices: { ...s.notices, [e.roomId]: [...(s.notices[e.roomId] ?? []), { id, text: e.text }] } }));
      setTimeout(() => set((s) => ({ notices: { ...s.notices, [e.roomId]: (s.notices[e.roomId] ?? []).filter((n) => n.id !== id) } })), 20_000);
      return;
    }
    case "presence":
      set((s) =>
        s.boot ? { boot: { ...s.boot, people: s.boot.people.map((p) => (p.id === e.userId ? { ...p, online: e.online } : p)) } } : {},
      );
      return;
  }
}

// ---- messages -----------------------------------------------------------------

export async function loadMessages(roomId: string, opts: { older?: boolean; fresh?: boolean } = {}): Promise<void> {
  const current = state.messages[roomId];
  if (current?.loading) return;
  if (current && !opts.older && !opts.fresh) return;
  const before = opts.older && current?.items[0] ? current.items[0].id : 0;
  set((s) => ({ messages: { ...s.messages, [roomId]: { items: current?.items ?? [], hasMore: current?.hasMore ?? false, loading: true } } }));
  try {
    const res = await api<{ messages: Message[]; tasks?: Task[]; hasMore: boolean }>(`/api/rooms/${roomId}/messages?limit=50${before ? `&before=${before}` : ""}`);
    if (res.tasks?.length) keepTasks(res.tasks);
    set((s) => {
      const items = opts.older ? [...res.messages, ...(s.messages[roomId]?.items ?? [])] : res.messages;
      return { messages: { ...s.messages, [roomId]: { items, hasMore: res.hasMore, loading: false } } };
    });
  } catch (err) {
    set((s) => ({ messages: { ...s.messages, [roomId]: { items: current?.items ?? [], hasMore: false, loading: false } } }));
    throw err;
  }
}

// ---- tasks ----------------------------------------------------------------------
export function keepTasks(list: Task[]): void {
  set((s) => ({ tasks: { ...s.tasks, ...Object.fromEntries(list.map((t) => [t.id, t])) } }));
}

/** Fetches one task again (after a change anywhere); drops it when it is no longer visible. */
export async function refreshTask(id: string): Promise<void> {
  try {
    const r = await api<{ task: Task }>(`/api/tasks/${id}`);
    set((s) => ({ tasks: { ...s.tasks, [id]: r.task }, tasksVersion: s.tasksVersion + 1 }));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      set((s) => {
        const { [id]: _gone, ...rest } = s.tasks;
        return { tasks: rest, tasksVersion: s.tasksVersion + 1 };
      });
    }
  }
}

export async function createTask(roomId: string, body: { title: string; notes?: string; ownerId: string | null; dueDate: string | null; fromMessageId?: number }): Promise<Task> {
  const r = await api<{ task: Task }>(`/api/rooms/${roomId}/tasks`, body);
  keepTasks([r.task]);
  return r.task;
}

export async function updateTask(id: string, patch: Partial<Pick<Task, "title" | "notes" | "status" | "ownerId" | "dueDate">>): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}`, patch, "PATCH");
  keepTasks([r.task]);
}

export async function linkTaskGithub(id: string, body: { link?: string; branch?: string | null; unlink?: "issue" | "pr" | "branch" }): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/github`, body, "PUT");
  keepTasks([r.task]);
}

export async function refreshTaskGithub(id: string): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/github/refresh`, {});
  keepTasks([r.task]);
}

export async function approveTask(id: string): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/approve`, {});
  keepTasks([r.task]);
}

/** The owner sends the work back to the coding worker with the review and an optional note. */
export async function sendBackTask(id: string, note: string): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/send-back`, { note });
  keepTasks([r.task]);
}

export async function confirmTask(id: string): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/confirm`, {});
  keepTasks([r.task]);
}

export async function shareTask(id: string, roomId: string): Promise<void> {
  const r = await api<{ task: Task }>(`/api/tasks/${id}/rooms`, { roomId });
  keepTasks([r.task]);
}

export async function unshareTask(id: string, roomId: string): Promise<void> {
  await api(`/api/tasks/${id}/rooms/${roomId}`, undefined, "DELETE");
  await refreshTask(id);
}

export async function sendMessage(roomId: string, body: string, fileIds: string[] = []): Promise<void> {
  const res = await api<{ message: Message }>(`/api/rooms/${roomId}/messages`, { body, fileIds });
  handleEvent({ type: "message.created", message: res.message });
}

export async function editMessage(id: number, body: string): Promise<void> {
  const res = await api<{ message: Message }>(`/api/messages/${id}`, { body }, "PATCH");
  handleEvent({ type: "message.updated", message: res.message });
}

/** An admin confirms what an agent proposed (it runs with the admin's rights), or cancels it. */
export async function decideAction(id: string, what: "confirm" | "cancel"): Promise<{ error?: string }> {
  return api<{ action: string; error?: string }>(`/api/actions/${id}/${what}`, {});
}

/** An admin approves, discards or edits an event agent's draft. */
export async function updateDraft(id: number, patch: { status?: "draft" | "approved" | "discarded"; body?: string }): Promise<void> {
  const res = await api<{ message: Message }>(`/api/messages/${id}/draft`, patch, "PATCH");
  handleEvent({ type: "message.updated", message: res.message });
}

export async function deleteMessage(id: number): Promise<void> {
  const res = await api<{ message: Message }>(`/api/messages/${id}`, undefined, "DELETE");
  handleEvent({ type: "message.updated", message: res.message });
}

export const REACTIONS = ["👍", "👌", "✅", "🎉", "👀", "🚀", "🙏", "❤️", "🙌", "🔥", "💯", "👏", "🤔", "💡", "😂", "😄", "🤝", "❓", "📌", "⏰", "✨", "🤯"];

export async function toggleReaction(id: number, emoji: string): Promise<void> {
  const res = await api<{ message: Message }>(`/api/messages/${id}/reactions`, { emoji });
  handleEvent({ type: "message.updated", message: res.message });
}

export function quoteInto(roomId: string, text: string | null) {
  set((s) => ({ quotes: { ...s.quotes, [roomId]: text } }));
}

export async function markRead(roomId: string): Promise<void> {
  const room = state.boot?.rooms.find((r) => r.id === roomId);
  if (!room?.isMember) return;
  const last = state.messages[roomId]?.items.at(-1)?.id ?? room.lastMessageId;
  if (!last || (room.unread === 0 && last <= room.lastReadMessageId)) return;
  updateRoom(roomId, (r) => ({ ...r, unread: 0, mentions: 0, lastReadMessageId: last }));
  await api(`/api/rooms/${roomId}/read`, { messageId: last }).catch(() => undefined);
}

export function setActiveRoom(id: string | null) {
  set({ activeRoomId: id });
}

// ---- rooms, people, invites -----------------------------------------------------

export async function createRoom(input: { name: string; purpose: string; color: string; isPrivate: boolean }): Promise<string> {
  const res = await api<{ id: string }>("/api/rooms", input);
  await loadBootstrap();
  return res.id;
}

export async function updateRoomSettings(id: string, input: Partial<{ name: string; purpose: string; color: string; isPrivate: boolean }>) {
  await api(`/api/rooms/${id}`, input, "PATCH");
  await loadBootstrap();
}

export async function joinRoom(id: string) {
  await api(`/api/rooms/${id}/join`, {});
  await loadBootstrap();
}

export async function addMembers(id: string, userIds: string[]) {
  await api(`/api/rooms/${id}/members`, { userIds });
  await loadBootstrap();
}

export async function setRoomHost(id: string, agentId: string | null) {
  await api(`/api/rooms/${id}/host`, { agentId }, "PUT");
  await loadBootstrap();
}

export async function removeMember(id: string, userId: string) {
  await api(`/api/rooms/${id}/members/${userId}`, undefined, "DELETE");
  await loadBootstrap();
}

export async function openDm(userId: string): Promise<string> {
  const res = await api<{ id: string }>("/api/dms", { userId });
  await loadBootstrap();
  return res.id;
}

export async function loadInvites() {
  const res = await api<{ invites: Invite[] }>("/api/invites");
  set({ invites: res.invites });
}

export async function sendInvite(input: { email: string; role: Role; roomIds: string[] }): Promise<"invited" | "added"> {
  const res = await api<{ status: "invited" | "added" }>("/api/invites", input);
  if (state.invites) await loadInvites();
  if (res.status === "added") await loadBootstrap();
  return res.status;
}

export async function revokeInvite(id: string) {
  await api(`/api/invites/${id}`, undefined, "DELETE");
  await loadInvites();
}

export async function signOut() {
  disconnect();
  await api("/api/auth/logout", {}).catch(() => undefined);
  set({ status: "signedOut", boot: null, messages: {}, invites: null });
}

// ---- selectors ----------------------------------------------------------------

export const personById = (s: State, id: string | null | undefined) => (id ? s.boot?.people.find((p) => p.id === id) : undefined);

export function roomTitle(s: State, r: Room): string {
  if (r.kind === "dm") return personById(s, r.dmWith)?.name ?? "Direct message";
  return r.name;
}
